import type { CSSProperties } from "react";

import type { OpenAPI } from "simplydo/interfaces";
import type { ChallengeWithIdeaTemplate } from "simplydo/interfaces/types/challengeTypes";
import type {
  IdeaAssessmentDetails as BaseIdeaAssessmentDetails,
  IdeaWithOwnerChallenge,
  IdeaWithVotes,
} from "simplydo/interfaces/types/ideaTypes";
import type { UserMe } from "simplydo/interfaces/types/userTypes";
import type { ButtonSize } from "simplydo/react/ui/button";

export type { IdeaWithOwnerChallenge, IdeaWithVotes };

export type IdeaAssessmentDetails = BaseIdeaAssessmentDetails;

/** An idea as listed by GET /challenges/{id}/ideas. */
export type LibIdeaListItem =
  OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"][number];

/** Assessment fields shared by listed ideas and GET /ideas/{id}. */
export type IdeaWithAssessments = Pick<
  LibIdeaListItem,
  | "_id"
  | "challenge"
  | "assessments"
  | "invitedExternalAssessors"
  | "lockedSubmittedAssessmentScore"
> & {
  assessmentDetails?: IdeaAssessmentDetails;
  assessmentFeedback?: OpenAPI.Schemas["Idea"]["assessmentFeedback"];
  ownerIdeaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  ownerChallenge?: Pick<LibIdeaOwnerChallenge, "organisation">;
};

export type LibIdeaDetail = OpenAPI.GET<"/ideas/{id}">["response"];

export type LibIdeaChallenge = ChallengeWithIdeaTemplate;

export type LibIdeaOwnerChallenge = NonNullable<
  OpenAPI.Schemas["ResponseGetIdea"]["ownerChallenge"]
>;

export type LibIdeaWithTags = Pick<
  LibIdeaListItem,
  "_id" | "challenge" | "tags" | "ownerTags"
>;

export type LibIdeaViewIdea = LibIdeaListItem & {
  /** Set once a "basic" idea has been enriched with full content */
  _enriched?: boolean;
};

export type LibIdeaProjectBoardEntry = NonNullable<
  LibIdeaListItem["projectManagement"]
>["boards"][number];

export type LibIdeaViewChallenge = ChallengeWithIdeaTemplate & {
  tagList?: NonNullable<OpenAPI.Schemas["AssignIdeaTagList"]["tagList"]>;
  voteLimit?: string | number;
};

export type IdeaAssessment = NonNullable<
  NonNullable<IdeaWithAssessments["assessments"]>[number]
>;

export type IdeaDisplayField = {
  _id?: string;
  id?: string;
  name?: string;
  type?: string;
  title?: string;
  value?: unknown;
};

export type IdeaCompanyVerificationReport =
  OpenAPI.Schemas["CreditsafeReport"] & {
    tempReportUrl?: string;
    reportVisible?: boolean;
    reportUrl?: string;
    report?: string;
  };

export type IdeaTeamStartChatProps = {
  idea: IdeaWithOwnerChallenge;
  onMessageThreadSelected?: (threadId: string) => void;
  style?: CSSProperties;
  size?: ButtonSize;
  fluid?: boolean;
  basic?: boolean;
};

export type IdeaChallengeHelpProps = { challengeId: string };

export type IdeaCompanyVerificationProps = {
  idea: IdeaWithOwnerChallenge;
  report: IdeaCompanyVerificationReport;
  updateReportUrl: (url: string) => void;
  field: IdeaDisplayField;
};

type UserIdeaOwnerChallenge = NonNullable<
  Exclude<
    OpenAPI.GET<"/users/{id}/ideas">["response"],
    unknown[]
  >["ideas"][number]["ownerChallenge"]
>;

/** A listed idea, or a user's idea with its owner challenge. */
export type IdeaCardViewIdea = LibIdeaViewIdea & {
  ownerChallenge?: Pick<
    UserIdeaOwnerChallenge,
    "_id" | "name" | "organisation"
  >;
};

/** The full challenge, or the owner challenge sent with a user's ideas. */
export type IdeaCardViewChallenge = Pick<
  UserIdeaOwnerChallenge,
  | "_id"
  | "organisation"
  | "voteLimit"
  | "tagList"
  | "votingVisibility"
  | "voteCountVisibility"
  | "ideaVisibility"
  | "ideaVisibilityLimits"
  | "preventIdeaComments"
> & { ownerIdeaTemplate?: LibIdeaViewChallenge["ownerIdeaTemplate"] };

export type IdeaCardViewProps = {
  idea: IdeaCardViewIdea;
  challenge: IdeaCardViewChallenge;
  onSelect?: (idea: IdeaCardViewIdea) => void;
  selectedIdeas?: string[];
  ideasVotedFor: number;
  setPreviewOpen?: (ideaId: string, options?: { toComments?: boolean }) => void;
};

export type IdeaAssessmentReportChallenge =
  | Pick<ChallengeWithIdeaTemplate, "ownerIdeaTemplate">
  | Pick<LibIdeaOwnerChallenge, "ownerIdeaTemplate">;

export type IdeaImpactsCache =
  OpenAPI.GET<"/challenges/{id}/ideas/impacts">["response"];

export type IdeaImpactsEntry = NonNullable<
  IdeaImpactsCache["ideaImpacts"][string]
>;

type IdeaTotalImpactItem = NonNullable<
  NonNullable<IdeaImpactsEntry["totalImpacts"]>
>[number];

export type IdeaImpactMeasure = IdeaTotalImpactItem;

export type ImpactStatementWithCreator = OpenAPI.Schemas["ImpactStatement"] & {
  ownerCreatedBy?: OpenAPI.Schemas["User"];
};

export type OrganisationImpact = OpenAPI.Schemas["OrganisationImpact"];

export type OrganisationImpactCategory =
  OpenAPI.Schemas["OrganisationImpactCategory"];

export type MappedImpactCategory = {
  _id?: string;
  name: string;
  order: number;
  impacts?: OrganisationImpact[];
  visible?: boolean;
};

export type ImpactStatementImpactsMap = NonNullable<
  ImpactStatementWithCreator["impacts"]
>;

export type ImpactStatementImpactValue =
  ImpactStatementImpactsMap[keyof ImpactStatementImpactsMap];

/** The impact fields impact texts are formatted from. */
export type FormattableImpact = {
  ownerImpact?: Pick<
    OrganisationImpact,
    "type" | "effect" | "name" | "maxWeight"
  >;
  ownerImpactCategory?: { name?: string };
  value?: string | number | null;
  choice?: {
    value?: string | number | null;
    toRange?: string | number;
    weight?: number;
  };
  frequency?: ImpactStatementImpactValue["frequency"];
  justification?: string;
};

export type StatementImpactEntry = ImpactStatementImpactValue;

export type CreditsafeCreateReportResponse =
  OpenAPI.POST<"/verifications/companies/reports">["response"] & {
    report?: OpenAPI.POST<"/verifications/companies/reports">["response"]["report"] & {
      tempReportUrl?: string;
    };
  };

export type CreditsafeReportResponse =
  CreditsafeCreateReportResponse["report"] & { tempReportUrl?: string };

export type DropdownFieldValue =
  | string
  | number
  | boolean
  | (string | number | boolean)[]
  | undefined;

export const dropdownStringValue = (
  value: DropdownFieldValue,
): string | undefined => {
  if (value === undefined || value === null || Array.isArray(value)) {
    return undefined;
  }
  return String(value);
};

export type IdeasLibUser = UserMe | null;

export type RequestJoinInvitationBody = {
  forType: "requestJoinIdea";
  forId: string;
  invitee: string;
  note?: string;
};

type OpenAPIInvitationCreateBody = NonNullable<
  OpenAPI.POST<"/invitations">["requestBody"]
>;

export const toInvitationCreateBody = (
  body: RequestJoinInvitationBody,
): OpenAPIInvitationCreateBody => body;

export type AssessmentReportSection = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>[number]
>;

export type AssessmentReportField = NonNullable<
  NonNullable<AssessmentReportSection["fields"]>[number]
>;

export type AssessmentFieldScore = { score?: number; notes?: string };
